'use client'

import { Suspense } from 'react'
import { useRouter } from 'next/navigation'

import {
  endSession,
  useSessionMarker,
  useSessionMove,
  useStudySessionView,
} from './use-study-session'

/**
 * The slim bar a block's page shows while it is part of a time-boxed session:
 * "Your 20-minute session · Block 2 of 3 · about 8 min left · End session".
 *
 * Mounted by the layouts of the pages a session opens (review, Foundations,
 * practice, the daily drill, a lesson); renders nothing unless the URL carries
 * a session marker and that session is still running.
 *
 * HONEST NUMBERS: "about 8 min left" is the planned size of the blocks still
 * to do, rounded to 5 like the picker's total — a plan, not a countdown, and
 * never time spent. End session stops early with no penalty: what was done
 * stays done, and the wrap-up shows it.
 *
 * TWO SURFACES. Review, practice and the daily drill put their questions on
 * FIXED-LIGHT "paper" cards (force-light: answer surfaces never go dark). The
 * bar used theme tokens, so in dark mode it was a dark strip sitting on top of
 * white question cards. On those pages (surface="paper") it is a light card of
 * its own in both themes, a sibling of the question cards. On theme-aware pages
 * (Foundations, lessons) it keeps theme tokens, which have dark counterparts.
 */
export type SessionBarSurface = 'theme' | 'paper'

/**
 * The bar's classes per surface, pure so a test can pin them: "paper" is
 * force-light with explicit light colours and no dark: variant; "theme" uses
 * only theme tokens (bg-muted, text-foreground, ...), so it follows the theme.
 */
export function sessionBarClasses(surface: SessionBarSurface): {
  outer: string
  inner: string
  separator: string
  muted: string
  end: string
  error: string
} {
  // The error sits OUTSIDE the force-light card: force-light pins every
  // descendant's text to slate-900, which would turn a red alert black.
  const error = 'w-full text-xs text-red-700 dark:text-red-300'
  // aria-disabled, not disabled: globals.css paints every [disabled] button
  // slate-800 (a dark-mode guard), a black chip on either surface.
  const endBase =
    'ml-auto rounded px-1.5 py-0.5 font-medium underline underline-offset-2 aria-disabled:opacity-60'
  if (surface === 'paper') {
    return {
      outer: 'px-4 pt-3',
      inner:
        'force-light mx-auto flex max-w-4xl flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-slate-200 bg-white px-4 py-1.5 text-xs text-slate-900 shadow-sm',
      separator: 'text-slate-400',
      muted: 'text-slate-600',
      end: `${endBase} text-slate-600 hover:text-slate-900`,
      error: `mx-auto mt-1 max-w-4xl ${error}`,
    }
  }
  return {
    outer: 'border-b border-border bg-muted/60 text-foreground',
    inner: 'mx-auto flex max-w-4xl flex-wrap items-center gap-x-2 gap-y-1 px-4 py-1.5 text-xs',
    separator: 'text-muted-foreground',
    muted: 'text-muted-foreground',
    end: `${endBase} text-muted-foreground hover:text-foreground`,
    error: `mx-auto max-w-4xl px-4 pb-1.5 ${error}`,
  }
}

export default function StudySessionBar({ surface = 'theme' }: { surface?: SessionBarSurface }) {
  // useSearchParams needs a boundary on statically rendered pages (lessons).
  return (
    <Suspense fallback={null}>
      <Bar surface={surface} />
    </Suspense>
  )
}

function Bar({ surface }: { surface: SessionBarSurface }) {
  const marker = useSessionMarker()
  // Outside a session the page is untouched: no fetch, no router.
  if (!marker) return null
  return <SessionBar id={marker.id} surface={surface} />
}

function SessionBar({ id, surface }: { id: string; surface: SessionBarSurface }) {
  const router = useRouter()
  const view = useStudySessionView(id)
  const { busy, error, run } = useSessionMove()

  if (!view || view.status !== 'active') return null

  async function end() {
    if (busy) return
    const goto = await run(() => endSession(id))
    if (goto) router.push(goto)
  }

  const c = sessionBarClasses(surface)
  return (
    <div
      role="region"
      aria-label="Study session"
      data-testid="study-session-bar"
      data-surface={surface}
      className={c.outer}
    >
      <div data-testid="study-session-bar-inner" className={c.inner}>
        <span className="font-medium">Your {view.minutes}-minute session</span>
        <span aria-hidden="true" className={c.separator}>
          ·
        </span>
        <span data-testid="study-session-bar-block">
          Block {Math.min(view.block + 1, view.blockCount)} of {view.blockCount}
        </span>
        {view.leftLabel && (
          <>
            <span aria-hidden="true" className={c.separator}>
              ·
            </span>
            <span className={c.muted}>{view.leftLabel}</span>
          </>
        )}
        <button
          type="button"
          onClick={() => void end()}
          aria-disabled={busy}
          data-track="study-session.end"
          className={c.end}
        >
          {busy ? 'Ending…' : 'End session'}
        </button>
      </div>
      {error && (
        <p role="alert" className={c.error}>
          {error}
        </p>
      )}
    </div>
  )
}
